import { Box, IconButton, Tooltip } from "@chakra-ui/react";
import {
  Children,
  FC,
  ReactElement,
  isValidElement,
  useCallback,
  useState,
} from "react";

import { track } from "lib/analytics";

const CopyIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/**
 * Extracts text content from a React element tree.
 */
function extractText(node: unknown): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (!isValidElement(node)) return "";
  const children = (node as ReactElement).props?.children;
  if (!children) return "";
  return Children.toArray(children).map(extractText).join("");
}

/**
 * Checks if the pre block contains a multi-line highlighted code block.
 */
function isHighlightedCode(children: unknown): boolean {
  const child = Children.only(children) as ReactElement;
  if (!isValidElement(child)) return false;
  const childProps = child.props as Record<string, unknown>;
  return typeof childProps.className === "string" && childProps.className.includes("hljs");
}

export const CodeBlock: FC<{ children: unknown }> = ({ children, ...props }) => {
  const [copied, setCopied] = useState(false);

  const hasHighlightedCode = (() => {
    try {
      return isHighlightedCode(children);
    } catch {
      return false;
    }
  })();

  const handleCopy = useCallback(() => {
    const text = extractText(children);
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);

      // Extract language from the code element's className (e.g., "hljs language-bash" → "bash")
      let language = "";
      try {
        const child = Children.only(children) as ReactElement;
        const childProps = child.props as Record<string, unknown>;
        const className = String(childProps.className || "");
        const match = className.match(/language-(\S+)/);
        if (match) language = match[1];
      } catch {
        // no-op
      }

      track("Code Block Copied", {
        page_path: window.location.pathname,
        language,
        content_preview: text.slice(0, 100),
      });
    });
  }, [children]);

  if (!hasHighlightedCode) {
    return (
      <Box as="pre" {...props} my={6}>
        {children}
      </Box>
    );
  }

  return (
    <Box position="relative" my={6}>
      <Box as="pre" {...props}>
        {children}
      </Box>
      <Tooltip label={copied ? "Copied!" : "Copy code"} placement="left">
        <IconButton
          aria-label="Copy code to clipboard"
          icon={copied ? <CheckIcon /> : <CopyIcon />}
          size="xs"
          variant="ghost"
          position="absolute"
          top={2}
          right={2}
          color={copied ? "success.base" : "gray.400"}
          _hover={{ color: copied ? "success.base" : "gray.600", bg: "gray.100" }}
          onClick={handleCopy}
        />
      </Tooltip>
    </Box>
  );
};
